import {
  SeaMessagesDTO,
  SeaReferenceTypesDTO,
  SeaRouteTypesDTO,
  SeaTransportTypesDTO,
  ShipmentStatusesDTO,
} from "entities/shared/seaCard.types";
import { DateTimeDTO, SearchCoordinateDTO, SearchReferenceDTO } from "entities/shared/shipmentCard.types";
import { ValueOf } from "shared/lib/typeCheck.lib";

export type SeaSealineNameDTO = string;

/** Qubictron ocean `status_code` values (see API docs). */
export type SeaStatusCodeDTO = ValueOf<typeof SeaMessagesDTO>;

export type SeaCountryDTO = {
  code: string;
  name: string;
};

export type SeaCoordinatesDTO = {
  lat: number | null;
  lng: number | null;
};

export type SeaLocationDTO = {
  id: number;
  name: string | null;
  state: string | null;
  country: SeaCountryDTO | null;
  unlocode: string | null;
  coordinates: SeaCoordinatesDTO | null;
};

/** Flattened location shape used by map / UI helpers. */
export type SeaLocationFlatDTO = {
  id: number;
  name: string | null;
  country: string | null;
  country_code: string | null;
  locode: string | null;
  lat: number | null;
  lng: number | null;
};

export type SeaFacilityDTO = {
  id: number;
  name: string | null;
  country?: SeaCountryDTO | null;
  unlocode: string | null;
  bic_code: string | null;
  smdg_code: string | null;
  coordinates: SeaCoordinatesDTO | null;
};

export type SeaTransportModeDTO = ValueOf<typeof SeaTransportTypesDTO> | "AIR";

export type SeaTransportDTO = {
  id: number;
  type: SeaTransportModeDTO;
  name: string | null;
  imo: number | null;
  call_sign: string | null;
  mmsi: number | null;
  flag: string | null;
};

export type SeaRoutePointEventDTO = {
  datetime: DateTimeDTO | null;
  is_actual: boolean | null;
  facility_id?: number | null;
  transport_mode?: SeaTransportModeDTO | null;
  transport_id?: number | null;
  voyage_number?: string | null;
};

export type SeaRouteNodeDTO = {
  location_id: number | null;
  arrival: SeaRoutePointEventDTO | null;
  departure: SeaRoutePointEventDTO | null;
};

export type SeaRouteSummaryDTO = {
  place_of_receipt: SeaRouteNodeDTO | null;
  first_port_of_loading: SeaRouteNodeDTO | null;
  transshipments: SeaRouteNodeDTO[];
  final_port_of_discharge: SeaRouteNodeDTO | null;
  place_of_delivery: SeaRouteNodeDTO | null;
};

export type SeaShipmentDTO = {
  reference_type: string;
  reference_number: SearchReferenceDTO;
  carrier: {
    code: string;
    name: string;
  };
  status: ValueOf<typeof ShipmentStatusesDTO>;
  updated_at: DateTimeDTO;
};

/** Qubictron event_code values. */
export const SeaEventCodesDTO = {
  ARRI: "ARRI",
  DEPA: "DEPA",
  LOAD: "LOAD",
  DISC: "DISC",
  GTIN: "GTIN",
  GTOT: "GTOT",
  STUF: "STUF",
  STRP: "STRP",
  PICK: "PICK",
  AVPU: "AVPU",
  DROP: "DROP",
  AVDO: "AVDO",
  INSP: "INSP",
  RSEA: "RSEA",
  RMVD: "RMVD",
  CUSS: "CUSS",
  CUSI: "CUSI",
  CUSR: "CUSR",
  CROS: "CROS",
  RECE: "RECE",
  DRFT: "DRFT",
  PENA: "PENA",
  PENU: "PENU",
  PENC: "PENC",
  CONF: "CONF",
  REJE: "REJE",
  APPR: "APPR",
  ISSU: "ISSU",
  SURR: "SURR",
  SUBM: "SUBM",
  VOID: "VOID",
  REQS: "REQS",
  CMPL: "CMPL",
  HOLD: "HOLD",
  RELS: "RELS",
  CANC: "CANC",
} as const;

export type SeaEventCodeDTO = ValueOf<typeof SeaEventCodesDTO> | string;

export const SeaLocationTypesDTO = {
  DEPOT: "DEPOT",
  PICKUP: "PICKUP",
  PLACE_OF_RECEIPT: "PLACE_OF_RECEIPT",
  FIRST_PORT_OF_LOADING: "FIRST_PORT_OF_LOADING",
  TRANSSHIPMENT_PORT: "TRANSSHIPMENT_PORT",
  FINAL_PORT_OF_DISCHARGE: "FINAL_PORT_OF_DISCHARGE",
  PLACE_OF_DELIVERY: "PLACE_OF_DELIVERY",
  DELIVERY: "DELIVERY",
  WAYPOINT: "WAYPOINT",
} as const;

export type SeaContainerEventDTO = {
  sequence: number;
  description: string;
  datetime: DateTimeDTO;
  is_actual: boolean;
  location_id: number | null;
  location_type: ValueOf<typeof SeaLocationTypesDTO> | null;
  facility_id: number | null;
  transport_mode: SeaTransportModeDTO | null;
  transport_id: number | null;
  voyage_number: string | null;
  type: "SHIPMENT" | "TRANSPORT" | "EQUIPMENT";
  event_code: SeaEventCodeDTO;
  empty_indicator: "EMPTY" | "FULL";
};

export type SeaRouteLegDTO = {
  sequence?: number;
  from_location_id?: number;
  to_location_id?: number;
  transport_mode?: SeaTransportModeDTO | null;
  transport_id?: number;
  /** Ordered [lat, lng] pairs. */
  route_path?: SearchCoordinateDTO[] | null;
};

export type SeaRouteDetailsDTO = {
  predicted_position?: {
    coordinates?: SeaCoordinatesDTO | null;
  } | null;
  latest_ais_data?: {
    transport_id?: number;
    coordinates?: SeaCoordinatesDTO | null;
    timestamp?: DateTimeDTO;
    departure?: { datetime: DateTimeDTO; is_actual: boolean; unlocode: string } | null;
    arrival?: { datetime: DateTimeDTO; is_actual: boolean; unlocode: string } | null;
  } | null;
  route_legs?: SeaRouteLegDTO[] | null;
};

export type SeaContainerDTO = {
  number: SearchReferenceDTO;
  size: number;
  type: string;
  iso_code: string;
  status: ValueOf<typeof ShipmentStatusesDTO> | string;
  events: SeaContainerEventDTO[];
  /** Working API (main): route_details is on the container. Docs also allow data-level. */
  route_details?: SeaRouteDetailsDTO | null;
};

export type SeaMetaDTO = {
  request?: {
    params?: {
      reference_number?: string;
      reference_type?: string;
      carrier_code?: string;
    };
  };
};

export type SeaResponseDTO = {
  success: boolean;
  status_code: SeaStatusCodeDTO;
  meta?: SeaMetaDTO;
  /**
   * Set by the back office, not the gateway, when the visitor has spent their
   * free searches. It arrives instead of a shipment and carries no status_code,
   * so the card must not be built from it - see SearchApiParser.
   */
  requestLimitReachedWebApp?: boolean;
  data?: {
    shipment?: SeaShipmentDTO;
    locations?: SeaLocationDTO[];
    facilities?: SeaFacilityDTO[];
    transports?: SeaTransportDTO[];
    route?: SeaRouteSummaryDTO;
    containers?: SeaContainerDTO[];
    route_details?: SeaRouteDetailsDTO | null;
  };
};

/** UI-normalized container event (used by EventList / vessel helpers). */
export type SeaEventDTO = {
  location: number | null;
  description: string;
  status: SeaEventCodeDTO;
  date: DateTimeDTO;
  actual: boolean;
  vessel: number | null;
  voyage: string | null;
  type: ValueOf<typeof SeaRouteTypesDTO> | null;
  transportType: ValueOf<typeof SeaTransportTypesDTO> | null;
  location_type?: ValueOf<typeof SeaLocationTypesDTO> | null;
};

export type DemurageDTO = {
  storage: {
    free_days: number | null;
    days_in_charge: number | null;
  };
  demurrage: {
    free_days: number | null;
    days_in_charge: number | null;
  };
  detention: {
    free_days: number | null;
    days_in_charge: number | null;
  };
};

/** UI-normalized container (keeps size_type for existing model mapping). */
export type SeaContainerViewDTO = {
  number: SearchReferenceDTO;
  size_type: string | null;
  iso_code: string;
  events: SeaEventDTO[];
  status: string;
  charges?: DemurageDTO | null;
};

export type SeaRouteDTO = {
  path: SearchCoordinateDTO[];
  type: ValueOf<typeof SeaRouteTypesDTO>;
  transport_type: ValueOf<typeof SeaTransportTypesDTO>;
};

export type SeaMetadataDTO = {
  type: ValueOf<typeof SeaReferenceTypesDTO> | null;
  number: SearchReferenceDTO;
  requested_number: SearchReferenceDTO;
  sealine: string;
  sealine_name: SeaSealineNameDTO | null;
  status: ValueOf<typeof ShipmentStatusesDTO> | null;
  updated_at: DateTimeDTO;
};
